import { BackgroundURLs } from "../realms/types/backgroundUrls.interface"
import { Realm } from "../realms/types/realm.interface"
import { Button } from "./components/button"
import { Favicon } from "./components/favicon"
import { FooterComponent } from "./components/footer"
import { FooterText } from "./components/footerText"
import { ForgotPassword } from "./components/forgotPassword"
import { FormWrapper } from "./components/formWrapper"
import { Header } from "./components/header"
import { ImageComponent } from "./components/image"
import { Input } from "./components/input"
import { Link } from "./components/link"
import { PageTitle } from "./components/pageTitle"
import { PoweredBy } from "./components/poweredBy"
import { SocialLogin } from "./components/socialLogin"
import { Text } from "./components/text"
import { getFontFamily } from "./theme.fonts"

import { TrackingEvent } from "./trackingEvent"
import { AlignmentEnum } from "./types/alignment.enum"
import { mobile, tablet } from "./types/breakpoint.constant"
import { EventInterface } from "./types/event.interface"
import { ScreenInterface } from "./types/screen.interface"
import { ThemeComponentsInterface } from "./types/themeComponents.interface"
import { ThemeServiceInterface } from "./types/themeService.interface"

export class Screen implements ScreenInterface {
    private service: ThemeServiceInterface
    private components: ThemeComponentsInterface
    private configs: Realm = {} as Realm
    private trackingEvent: TrackingEvent

    constructor(service: ThemeServiceInterface, components) {
        this.service = service
        this.components = components
        this.trackingEvent = new TrackingEvent()
    }

    private async setConfigs(): Promise<void> {
        this.configs = await this.service.getThemeSettings()
    }

    private setLoading(force?: boolean): void {
        this.components.loader.classList.toggle("hidden", !force)
    }

    private isTreatedBackground(
        background: string | BackgroundURLs | undefined
    ): background is BackgroundURLs {
        return (background as BackgroundURLs)?.mobile !== undefined
    }

    private defineBackgroundSize(): string {
        const screenWidth = window.innerWidth
        const backgroundImage =
            this.configs.backgroundUrl ?? this.configs.backgroundUrl

        if (this.isTreatedBackground(backgroundImage)) {
            if (screenWidth <= mobile && backgroundImage?.mobile) {
                return `url('${backgroundImage?.mobile}')`
            }

            if (screenWidth <= tablet && backgroundImage?.tablet) {
                return `url('${backgroundImage?.tablet}')`
            }

            return `url('${backgroundImage?.desktop}')`
        }

        return `url('${backgroundImage}')`
    }

    private setBackground(): void {
        if (!this.components.brandBackground) {
            return
        }

        if (!this.configs.backgroundUrl && !this.configs.backgroundUrl) {
            this.components.brandBackground.style.backgroundImage = "none"
            return
        }

        this.components.brandBackground.classList.remove("default")
        this.components.brandBackground.style.backgroundImage =
            this.defineBackgroundSize()
    }

    private setDesignSystem(): void {
        if (this.configs.designSystem) {
            this.components.root.classList.add(
                `ds-${this.configs.designSystem}`
            )
        }
    }

    private setMode(): void {
        if (!this.configs.mode) return

        this.components.root?.classList.remove(
            `${this.service.defaultMode}-mode`
        )
        this.components.root?.classList.add(
            `${this.configs.mode?.toLocaleLowerCase()}-mode`
        )
    }

    private setLayoutPosition(): void {
        if (this.configs.layout === "CENTER") {
            document
                .querySelector("body")
                ?.classList.add("centered-login-wrapper")
        }
    }

    private async setBaseFontFamily(): Promise<void> {
        const font = this.configs.baseFont

        if (font) {
            const root = document.querySelector(":root") as HTMLElement

            const fontFamily = await getFontFamily(font)

            if (fontFamily) {
                root.style.setProperty("--font-family", fontFamily)
                root.style.setProperty("--hc-font-family-primary", fontFamily)
            }
        }
    }

    private renderFormWrapperComponents() {
        const brandName = new Text({
            elementID: "brand-name",
            title: this.configs.brandName,
            isVisible: !this.configs.logoHeaderUrl,
            fontFamily: this.configs.customFont
        })

        const screenTitle = new Text({
            elementID: "screen-title",
            isVisible: true,
            fontFamily: this.configs.customFont,
            textTransform: this.configs.wrapperCard?.textTransform,
            color: this.configs.colors?.text,
            fontSize: this.configs.wrapperCard?.titleFontSize
        })

        const signupHoverColor =
            this.configs.signupLink?.hoverColor ?? this.configs.link?.hoverColor

        const linkSignup = new Link({
            elementID: "create-account",
            isVisible: true,
            color: this.configs.signupLink?.color ?? this.configs.link?.color,
            hoverColor: signupHoverColor,
            url: this.configs.signupLink?.url,
            decoration: this.configs.link?.textDecoration,
            decorationHoverColor:
                this.configs.link?.decorationHoverColor ?? signupHoverColor
        })

        const forgotPasswordHoverColor =
            this.configs.forgotPasswordLinkComponent?.hoverColor ??
            this.configs.link?.hoverColor

        const forgotPassword = new ForgotPassword({
            elementID: "forgot-password",
            isVisible: true,
            color:
                this.configs.forgotPasswordLinkComponent?.color ??
                this.configs.link?.color,
            hoverColor: forgotPasswordHoverColor,
            url: this.configs.forgotPasswordLink,
            position: this.configs.forgotPasswordLinkComponent?.position,
            alignment: this.configs.forgotPasswordLinkComponent?.alignment,
            decoration: this.configs.forgotPasswordLinkComponent?.decoration,
            decorationHoverColor: this.configs.link?.decorationHoverColor
        })

        const textDontHaveAccount = new Text({
            elementID: "signup-text",
            isVisible: true,
            color: this.configs.colors?.text
        })

        const footer = new Text({
            elementID: "wrapper-footer",
            isVisible: this.configs.wrapperCard?.footer
        })

        const poweredByImage = new ImageComponent({
            elementID: "powered-by-image",
            urlImage:
                this.configs.mode?.toLowerCase() === "dark"
                    ? this.configs.logoPoweredByUrl?.dark
                    : this.configs.logoPoweredByUrl?.light,
            isVisible: true
        })

        const poweredby = new PoweredBy(
            {
                elementID: "powered-by",
                isVisible: this.configs.showComponents?.poweredBy
            },
            { image: poweredByImage }
        )

        const brandLogo = new ImageComponent({
            elementID: "brand-logo",
            urlImage:
                this.configs.mode?.toLowerCase() === "dark"
                    ? this.configs.logoHeaderUrl?.dark
                    : this.configs.logoHeaderUrl?.light,
            isVisible: !this.configs.showComponents?.header
        })

        const input = new Input({
            elementID: "hc-form-field",
            labelColor: this.configs.input?.labelColor,
            borderColor: this.configs.input?.borderColor,
            showPlaceholder: this.configs.input?.showPlaceholder
        })

        const formWrapper = new FormWrapper(
            {
                elementID: "wrapper-card",
                boxBorder: this.configs.wrapperCard?.boxBorder,
                boxShadow: this.configs.wrapperCard?.boxShadow,
                width: this.configs.wrapperCard?.width,
                signupUrl: this.configs.signupLink?.url,
                marginTop: this.configs.wrapperCard?.marginTop
            },
            {
                brandName: brandName,
                poweredBy: poweredby,
                footer: footer,
                brandLogo: brandLogo,
                screenTitle: screenTitle,
                textDontHaveAccount: textDontHaveAccount,
                forgotPassword: forgotPassword,
                linkSignup: linkSignup,
                input: input
            }
        )

        formWrapper.render()
    }

    private renderHeaderComponents() {
        const title = new Text({
            elementID: "header-title",
            title: this.configs.brandName,
            isVisible: !this.configs.logoHeaderUrl,
            fontFamily: this.configs.customFont,
            color: this.configs.header?.textColor
        })

        const image = new ImageComponent({
            elementID: "login-custom-header-logo",
            urlImage:
                this.configs.mode?.toLowerCase() === "dark"
                    ? this.configs.logoHeaderUrl?.dark
                    : this.configs.logoHeaderUrl?.light,
            isVisible: !!this.configs.logoHeaderUrl
        })

        const helpButton = new Button({
            elementID: "help-button",
            isVisible: this.configs?.showComponents?.helpButton
        })

        if (this.configs.showComponents?.helpButton) {
            this.configs.header = {
                ...this.configs.header,
                alignment: AlignmentEnum.SPACE_BETWEEN
            }
        }

        const header = new Header(
            {
                elementID: "login-custom-header",
                backgroundColor: this.configs.colors?.primary,
                isVisible: this.configs.showComponents?.header,
                alignment: this.configs.header?.alignment ?? AlignmentEnum.LEFT,
                borderBottom: this.configs.header?.borderBottom
            },
            {
                title,
                image,
                helpButton
            }
        )
        header.render()
    }

    private renderFooterComponents() {
        const footerText = new FooterText({
            elementID: "login-footer-copyright",
            isVisible: true,
            branding: this.configs.brandName,
            title: this.configs.footer?.copyright,
            color: this.configs.footer?.textColor
        })
        const footer = new FooterComponent(
            {
                elementID: "login-custom-footer",
                backgroundColor: this.configs.colors?.primary,
                alignment: this.configs.footer?.alignment,
                isVisible: this.configs.showComponents?.footer
            },
            {
                footerTitle: footerText
            }
        )
        footer.render()
    }

    private renderFavicon() {
        const favicon = new Favicon({
            elementID: "favicon",
            url: this.configs.faviconUrl
        })

        favicon.render()
    }

    private renderSocialProviders() {
        if (!this.configs.enableFeatures) return

        const loginProviders = [
            {
                elementId: "signin-google",
                isShow: this.configs.enableFeatures.signupGoogle
            },
            {
                elementId: "signin-apple",
                isShow: this.configs.enableFeatures.signupApple
            }
        ]

        const socialLogin = new SocialLogin({
            elementID: "social-login-custom",
            loginProviders
        })

        socialLogin.render()
    }

    private renderSubheadings() {
        if (this.configs.showSubheading) {
            const subheadingEntries = Object.entries(
                this.configs.showSubheading
            )

            for (const [subheadingKey, isVisible] of subheadingEntries) {
                if (isVisible) continue

                const subheading = new Text({
                    elementID: subheadingKey,
                    isVisible: !!isVisible,
                    fontFamily: this.configs.baseFont,
                    color: this.configs.colors?.text
                })

                subheading.render()
            }
        }
    }

    async render() {
        try {
            this.setLoading(true)

            await this.setConfigs()

            const pageTitle = new PageTitle(this.configs.pageTitle)
            pageTitle.render()

            this.setDesignSystem()
            this.setMode()
            this.setBackground()
            this.setLayoutPosition()
            this.setBaseFontFamily()

            this.renderFavicon()
            this.renderFormWrapperComponents()
            this.renderHeaderComponents()
            this.renderFooterComponents()
            this.renderSocialProviders()
            this.renderSubheadings()

            const button = new Button({
                elementID: "root",
                backgroundColor:
                    this.configs.button?.backgroundColor ??
                    this.configs.colors?.secondary,
                hoverColor:
                    this.configs.button?.hoverColor ??
                    this.configs.colors?.secondary,
                width: this.configs.button?.width,
                minWidth: this.configs.button?.minWidth,
                isVisible: true
            })

            button.render()
        } catch (error: any) {
            console.error(error)

            const event: EventInterface = {
                brand: this.service.brand,
                error: {
                    message: error?.message || "",
                    path: window.location.href,
                    code: "SSO_RUNTIME"
                }
            }
            this.trackingEvent.send(event)
            return
        } finally {
            this.setLoading(false)
        }
    }
}
